'use client';

import { useCallback, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import dynamic from 'next/dynamic';
import { Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';

import { useCollections } from '@/hooks/use-collections';
import { TWork, type TChapterWithoutContent } from '@/features/fanfic-page/types/fanfic';
import CollectionSelectionDialog from '@/components/collection-selection-dialog';
import { timeToReadByTotalWords } from '@/lib/time-to-read';
import {
  useFanficPageReadingProgress,
  useFanficPageReader,
  useFanficPageTabs,
  useFanficRating,
  useFanficReaderNavigation,
} from '@/features/fanfic-page/hooks';
import { HeroSection } from '@/features/fanfic-page/components/hero-section';
import { FanficTabs } from '@/features/fanfic-page/components/fanfic-tabs';
import { FanficRating } from '@/features/fanfic-page/components/fanfic-rating';
import { buildWorkBreadcrumbs } from '@/lib/breadcrumbs';
import { cn } from '@/lib/utils';
import type { TRelatedFanfic } from '@/features/fanfic-page/requests';
import { SimilarFanficsSection } from '@/features/fanfic-page/components';
import {
  resolveFirstPublishedChapter,
  resolveWorkPageDeepLink,
  type WorkPageDeepLink,
} from '@/features/fanfic-page/utils';

const DEFAULT_WORK_PAGE_DEEP_LINK = resolveWorkPageDeepLink();

const FullscreenReaderV2 = dynamic(
  () =>
    import('@/components/fanfic-page/fullscreen-reader-v2').then((mod) => ({
      default: mod.FullscreenReaderV2,
    })),
  {
    loading: () => (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <Loader2 className="w-14 h-14 text-[#505CFF] animate-spin" />
      </div>
    ),
    ssr: false,
  },
);

export interface FanficPageClientProps {
  fanfic: TWork;
  relatedFanfics: TRelatedFanfic[];
  preview?: boolean;
  workPathPrefix?: '/fanfics' | '/originals';
  /** Published chapters from SSR for Read href + chapters TOC without JS. */
  initialChapters?: TChapterWithoutContent[];
  /** SSR-resolved deep-link from page searchParams (tab / review focus). */
  initialDeepLink?: WorkPageDeepLink;
}

export const FanficPageClient = ({
  fanfic,
  relatedFanfics,
  preview = false,
  workPathPrefix = '/fanfics',
  initialChapters = [],
  initialDeepLink = DEFAULT_WORK_PAGE_DEEP_LINK,
}: FanficPageClientProps) => {
  const router = useRouter();
  const fanficId = fanfic.id.toString();
  const [focusReviewComposer, setFocusReviewComposer] = useState(
    initialDeepLink.focusReviewComposer,
  );
  const highlightReviewId = initialDeepLink.highlightReviewId;
  const timeToRead = timeToReadByTotalWords(fanfic?.totalWordCount);
  const isRating18 = fanfic.rating === '18';
  const breadcrumbItems = buildWorkBreadcrumbs(fanfic.title || 'Без названия', workPathPrefix);
  const firstPublishedChapter = resolveFirstPublishedChapter(initialChapters);

  const { progressByChapter, updateReadingProgress, handleProgressSync } =
    useFanficPageReadingProgress({
      fanficId,
    });

  const { collectionData, addToCollection, updateStatus, updateRating, removeFromCollection } =
    useCollections(fanficId);

  const {
    userRating,
    hoveredRating,
    isOpenCollectionDialog,
    handleStarClick,
    handleStarHover,
    handleStarLeave,
    closeCollectionDialog,
  } = useFanficRating({
    initialRating: fanfic?.ratingItem?.rating || 0,
    isInCollection: Boolean(collectionData.isInCollection),
    updateRating,
  });

  const { activeTab, setActiveTab } = useFanficPageTabs({
    initialTab: initialDeepLink.activeTab,
  });
  const [reviewsHasNextPage, setReviewsHasNextPage] = useState(true);
  const handleReviewsHasNextPageChange = useCallback((hasNextPage: boolean) => {
    setReviewsHasNextPage(hasNextPage);
  }, []);

  const {
    selectedChapterId,
    setSelectedChapterId,
    isFullscreenOpen,
    setIsFullscreenOpen,
    closeFullscreenReader,
    readerChapters,
    chaptersWithContent,
    isLoadingChapters,
    handleLoadMoreChapters,
    handleChapterSelect,
    handleOpenFullscreen,
  } = useFanficPageReader({
    workId: fanficId,
    chaptersCount: fanfic.chaptersCount ?? 0,
    workPathPrefix,
  });

  const openReviewsComposer = () => {
    closeFullscreenReader({ restoreHistory: false });
    setActiveTab('reviews');
    setFocusReviewComposer(true);
    router.replace(`${workPathPrefix}/${fanfic.id}?tab=reviews&reviewEntry=compose`, {
      scroll: false,
    });
  };

  const { readerUrl, handleReadClick, handleAuxClick } = useFanficReaderNavigation({
    fanficId,
    workPathPrefix,
    firstChapterId: firstPublishedChapter?.id,
  });

  return (
    <div className="min-h-screen bg-background">
      <div className="relative">
        <HeroSection
          fanfic={fanfic}
          preview={preview}
          breadcrumbItems={breadcrumbItems}
          collectionData={collectionData}
          updateRating={updateRating}
          updateStatus={updateStatus}
          addToCollection={addToCollection}
          removeFromCollection={removeFromCollection}
          handleReadClick={handleReadClick}
          handleAuxClick={handleAuxClick}
          readerUrl={readerUrl}
          firstChapterShouldIndexing={
            firstPublishedChapter?.shouldIndexing ?? readerChapters[0]?.shouldIndexing ?? false
          }
        />

        <div
          className={cn(
            'w-full bg-white relative z-10 rounded-2xl min-h-screen flex flex-col',
            'min-[1461px]:-mt-9',
            'min-[1000px]:max-[1460px]:mt-2',
            'max-[999px]:mt-0',
          )}
        >
          <div className="mb-[58px] pt-4 px-2 min-[1000px]:px-4 min-[1000px]:pt-8 grid min-[1000px]:grid-cols-[1fr_max-content] gap-4 min-[1000px]:ml-[380px] min-[1000px]:pr-10 flex-1 min-h-0">
            <FanficTabs
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              fanfic={fanfic}
              selectedChapterId={selectedChapterId}
              progressByChapter={progressByChapter}
              updateReadingProgress={updateReadingProgress}
              onChapterSelect={setSelectedChapterId}
              onOpenFullscreen={handleOpenFullscreen}
              timeToRead={timeToRead}
              preview={preview}
              onReviewsHasNextPageChange={handleReviewsHasNextPageChange}
              focusReviewComposer={focusReviewComposer}
              highlightReviewId={highlightReviewId}
              initialChapters={initialChapters}
            />

            <div className="hidden min-[1000px]:block min-[1000px]:w-225px xl:w-[325px]">
              <CollectionSelectionDialog
                isOpen={isOpenCollectionDialog}
                onClose={closeCollectionDialog}
                onConfirm={(status, isPrivate) => {
                  addToCollection(status, isPrivate);
                  closeCollectionDialog();
                }}
                postId={fanficId}
                chaptersCount={fanfic.chaptersCount}
                title={
                  collectionData.isInCollection
                    ? 'ОБНОВИТЬ БИБЛИОТЕКУ?'
                    : 'ДОБАВИТЬ В ВАШУ БИБЛИОТЕКУ?'
                }
              />
              <FanficRating
                userRating={userRating}
                hoveredRating={hoveredRating}
                collectionData={collectionData}
                handleStarClick={handleStarClick}
                handleStarHover={handleStarHover}
                handleStarLeave={handleStarLeave}
                preview={preview}
              />
            </div>
          </div>

          {!preview && (activeTab !== 'reviews' || !reviewsHasNextPage) && (
            <SimilarFanficsSection relatedFanfics={relatedFanfics} activeTab={activeTab} />
          )}
        </div>

        <AnimatePresence>
          {isFullscreenOpen && (
            <FullscreenReaderV2
              key={String(fanfic.id)}
              open={isFullscreenOpen}
              onOpenChange={setIsFullscreenOpen}
              chaptersWithoutContent={readerChapters}
              chaptersWithContent={chaptersWithContent}
              selectedChapterId={selectedChapterId || readerChapters[0]?.id || ''}
              setSelectedChapterId={setSelectedChapterId}
              fanficId={fanficId}
              author={fanfic.authors?.[0] || null}
              views={fanfic.readersCount || fanfic.statistics?.readersCount || 0}
              comments={fanfic.statistics?.commentsCount || 0}
              onProgressSync={handleProgressSync}
              fetchProgress={false}
              initialProgressByChapter={progressByChapter}
              type="full"
              onLoadMoreChapters={handleLoadMoreChapters}
              isLoadingMoreChapters={isLoadingChapters}
              onChapterSelect={handleChapterSelect}
              price={fanfic.price}
              postId={fanfic.id}
              workPathPrefix={workPathPrefix}
              isRating18={isRating18}
              onLeaveReview={openReviewsComposer}
            />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default FanficPageClient;
